import { useState, useCallback, useEffect, type ReactNode } from 'react';
import type { AuthState, UserProfile } from '@/shared/schemas';
import { AuthContext } from '../state/auth-context';
import { verifySession as verifySessionApi } from '../api/verify-session';

const AUTH_DOMAIN = 'https://auth.hey.sh';

interface AuthProviderProps {
  readonly children: ReactNode;
}

/**
 * Auth provider that integrates with auth.hey.sh SSO.
 * Checks for session cookie and decodes JWT to get user info.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>('loading');
  const [user, setUser] = useState<UserProfile | null>(null);

  const checkSession = useCallback(() => {
    const result = verifySessionApi();

    if (result.ok) {
      setState('authenticated');
      setUser(result.value);
    } else {
      setState('unauthenticated');
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // This is intentional: we need to check session on mount
    // eslint-disable-next-line react-hooks/set-state-in-effect
    checkSession();
  }, [checkSession]);

  const signIn = useCallback(() => {
    const continueUrl = encodeURIComponent(window.location.href);
    window.location.href = `${AUTH_DOMAIN}/login?continue=${continueUrl}`;
  }, []);

  const signOut = useCallback(() => {
    const continueUrl = encodeURIComponent(window.location.origin);
    window.location.href = `${AUTH_DOMAIN}/logout?continue=${continueUrl}`;
  }, []);

  return (
    <AuthContext.Provider value={{ state, user, signIn, signOut }}>{children}</AuthContext.Provider>
  );
}
